import { useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Slider,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PaletteIcon from "@mui/icons-material/Palette";
import WhatshotIcon from "@mui/icons-material/Whatshot";

import { FireSimulation } from "../../types/FireSimulation";
import {
  SceneAnimationView,
  SimAnimationMetric,
  StorySceneAnimation,
} from "../../types/storyMap";
import { usePercentileMember } from "../../api/storyMaps";
import { useSimulationResultsFor } from "../../api/simulations";
import { extractIgnitionPoints } from "./ignitionPoints";
import { entryProjectId } from "./layerEntries";
import ProjectSelect from "./blocks/ProjectSelect";
import type { ScopeLists } from "./useScopeLists";
import type { StoryScopeInfo } from "./useStoryScope";
import HelpTip, { HelpLabel } from "./HelpTip";
import { useUnitSystem } from "../../hooks/useUnitSystem";
import { formatArea } from "../../utils/units";

const TEAL = "#00d4ad";

const METRIC_OPTIONS: { value: SimAnimationMetric; label: string }[] = [
  { value: "ros", label: "Rate of spread" },
  { value: "intensity", label: "Fireline intensity" },
  { value: "flame_height", label: "Flame height" },
];

/**
 * Percentile of the ensemble's final-burned-area distribution: the pN member
 * is the run whose final burned area is closest to the N-th percentile.
 */
const PERCENTILE_OPTIONS: { value: number; label: string }[] = [
  { value: 10, label: "p10 — mildest 10%" },
  { value: 30, label: "p30 — below median" },
  { value: 50, label: "p50 — median outcome" },
  { value: 70, label: "p70 — above median" },
  { value: 90, label: "p90 — worst 10%" },
];

/** Swatch row: null = the metric palette; a hex = flat colour tint. */
const COLOR_OPTIONS: { value: string | null; label: string }[] = [
  { value: null, label: "Metric colours" },
  { value: "#00d4ad", label: "Teal" },
  { value: "#f6861c", label: "Orange" },
  { value: "#4a90e2", label: "Blue" },
  { value: "#d81b60", label: "Magenta" },
];

/** Member count for an ensemble sim record (SavedSimulationsModal convention). */
function ensembleMemberCount(sim: FireSimulation | undefined): number {
  if (!sim?.is_ensemble) return 0;
  return sim.ensemble_stats?.members ?? sim.ensemble_members ?? 0;
}

interface EntryCardProps {
  entry: StorySceneAnimation;
  index: number;
  count: number;
  selected: boolean;
  onSelect: () => void;
  onChange: (patch: Partial<StorySceneAnimation>) => void;
  onMove: (delta: number) => void;
  onDelete: () => void;
  scope: StoryScopeInfo;
  lists: ScopeLists;
}

const NO_SIMS: FireSimulation[] = [];

/**
 * One animation entry's editor card. A component (not inline JSX) so each
 * entry gets its own hook instances — percentile resolution and the ignition
 * readout are per entry.
 */
function EntryCard({
  entry,
  index,
  count,
  selected,
  onSelect,
  onChange,
  onMove,
  onDelete,
  scope,
  lists,
}: EntryCardProps) {
  const unitSystem = useUnitSystem();
  // The project this entry's simulation belongs to: its own (folder and
  // organisation-wide stories), else the story's project.
  const entryPid = entryProjectId(entry, scope.defaultProjectId);
  const projectId = entryPid !== null ? String(entryPid) : undefined;
  useEffect(() => {
    if (entryPid !== null) lists.requestProject(entryPid);
  }, [entryPid, lists]);
  const simulations = entryPid !== null ? lists.simulationsByProject[String(entryPid)] ?? NO_SIMS : NO_SIMS;
  const sim = useMemo(
    () => simulations.find((s) => s.simulation_id === entry.simulationId),
    [simulations, entry.simulationId]
  );
  const isEnsemble = !!sim?.is_ensemble;
  const memberCount = ensembleMemberCount(sim);
  const hasEnsembleFrames = !!sim?.has_ensemble_frames;

  const view: SceneAnimationView = isEnsemble ? entry.view : "single";
  const percentile = entry.percentile ?? 50;

  // Ignition readout for this entry's simulation (cache shared with the
  // builder map's marker fetch for the selected entry).
  const { data: resultsData } = useSimulationResultsFor(entryPid, entry.simulationId);
  const ignitionPoints = useMemo(
    () => extractIgnitionPoints(resultsData?.data?.results),
    [resultsData]
  );
  const selectedPoint = useMemo(
    () =>
      entry.partition != null
        ? ignitionPoints.find(
            (p) => String(p.partition) === String(entry.partition)
          ) ?? null
        : null,
    [ignitionPoints, entry.partition]
  );

  // Resolve the pN member (final-burned-area distribution) and cache it into
  // content so the preview/viewer doesn't have to re-resolve.
  const percentileQuery = usePercentileMember(
    projectId,
    entry.simulationId,
    percentile,
    isEnsemble && view === "percentile"
  );
  const resolvedMember = percentileQuery.data?.member;
  useEffect(() => {
    if (view !== "percentile" || resolvedMember == null) return;
    if (entry.resolvedMember === resolvedMember) return;
    onChange({ resolvedMember });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, resolvedMember, entry.resolvedMember]);

  return (
    <Paper
      variant="outlined"
      onClick={onSelect}
      sx={{
        p: 1.5,
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        cursor: "pointer",
        borderColor: selected ? TEAL : "divider",
        borderWidth: selected ? 2 : 1,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, color: selected ? TEAL : "text.secondary" }}
        >
          Simulation {index + 1}
          {index === 0 && count > 1 ? " · bottom" : ""}
          {index === count - 1 && count > 1 ? " · top" : ""}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Move down the stack (drawn earlier)" arrow>
          <span>
            <IconButton
              size="small"
              disabled={index === 0}
              onClick={(e) => {
                e.stopPropagation();
                onMove(-1);
              }}
            >
              <KeyboardArrowUpIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Move up the stack (drawn later)" arrow>
          <span>
            <IconButton
              size="small"
              disabled={index >= count - 1}
              onClick={(e) => {
                e.stopPropagation();
                onMove(1);
              }}
            >
              <KeyboardArrowDownIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Remove this simulation" arrow>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <ProjectSelect
        scope={scope}
        value={entryPid}
        onChange={(projectId) =>
          // A simulation belongs to a project: a new project means choosing again.
          onChange({
            projectId,
            simulationId: null,
            partition: null,
            resolvedMember: null,
            view: "single",
          })
        }
      />
      <TextField
        select
        label="Simulation"
        size="small"
        value={entry.simulationId ?? ""}
        disabled={scope.isScoped && entryPid === null}
        onChange={(e) =>
          // Switching sims invalidates the picked partition/percentile/view.
          onChange({
            simulationId: e.target.value || null,
            partition: null,
            resolvedMember: null,
            view: "single",
          })
        }
      >
        <MenuItem value="">None</MenuItem>
        {simulations.map((simulation) => (
          <MenuItem key={simulation.simulation_id} value={simulation.simulation_id}>
            {simulation.name || "Unnamed simulation"}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label="Metric"
        size="small"
        value={entry.metric}
        onChange={(e) => onChange({ metric: e.target.value as SimAnimationMetric })}
      >
        {METRIC_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      {isEnsemble && (
        <Box>
          <HelpLabel
            label={`View (${memberCount} ensemble members)`}
            title="Ensemble view"
          >
            <p>
              This simulation is an ensemble — {memberCount} runs of the same
              fire with the conditions perturbed. Choose what the reader sees.
            </p>
            <p>
              <strong>Single run</strong> — one member, played like an ordinary
              simulation.
            </p>
            <p>
              <strong>Percentile</strong> — the member whose final burnt area
              sits at the percentile you pick. p50 is a typical outcome, p90 a
              bad one. Still a single real fire, so it animates normally.
            </p>
            <p>
              <strong>Confidence</strong> — not one fire but all of them at
              once: each cell shaded by the share of members that burnt it.
              Use it to show where fire went reliably versus occasionally.
            </p>
          </HelpLabel>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={view}
            onChange={(_, value: SceneAnimationView | null) => {
              if (!value) return;
              onChange({
                view: value,
                percentile: entry.percentile ?? 50,
                resolvedMember: value === "percentile" ? null : undefined,
              });
            }}
            sx={{ mt: 0.5 }}
          >
            <ToggleButton value="single" sx={{ textTransform: "none" }}>
              Single run
            </ToggleButton>
            <ToggleButton value="percentile" sx={{ textTransform: "none" }}>
              Percentile
            </ToggleButton>
            <ToggleButton
              value="confidence"
              disabled={!hasEnsembleFrames}
              sx={{ textTransform: "none" }}
            >
              Confidence
            </ToggleButton>
          </ToggleButtonGroup>
          {!hasEnsembleFrames && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Confidence needs the ensemble burn-probability stack (not
              available for this simulation).
            </Typography>
          )}
        </Box>
      )}

      {isEnsemble && view === "percentile" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <TextField
            select
            label="Percentile (final burned area)"
            size="small"
            value={percentile}
            onChange={(e) =>
              onChange({
                percentile: Number(e.target.value),
                resolvedMember: null,
              })
            }
          >
            {PERCENTILE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          {percentileQuery.isFetching ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={12} />
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Resolving p{percentile} member…
              </Typography>
            </Box>
          ) : percentileQuery.data ? (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              p{percentile} → member {percentileQuery.data.member + 1}
              {percentileQuery.data.burned_area_ha != null
                ? ` · ${formatArea(
                    Math.round(percentileQuery.data.burned_area_ha),
                    unitSystem
                  )} burnt`
                : ""}
            </Typography>
          ) : percentileQuery.isError ? (
            <Typography variant="caption" sx={{ color: "warning.main" }}>
              Couldn't resolve the percentile member for this simulation.
            </Typography>
          ) : null}
        </Box>
      )}

      {view === "single" && entry.simulationId && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <HelpLabel label="Ignition" title="Ignition">
            <p>
              A simulation can start from several ignition points, and each one
              produces its own fire. This scene animates one of them.
            </p>
            <p>
              Select this card, then click an ignition marker on the map to
              choose. With only one ignition it's picked for you.
            </p>
          </HelpLabel>
          {selectedPoint ? (
            <Chip
              size="small"
              icon={<WhatshotIcon />}
              color="secondary"
              label={`Ignition ${selectedPoint.label}`}
              sx={{ alignSelf: "flex-start" }}
            />
          ) : (
            <Typography variant="caption" sx={{ color: "warning.main" }}>
              {ignitionPoints.length === 0
                ? "Loading ignition points…"
                : selected
                ? "Pick an ignition by clicking a marker on the map."
                : "Select this card, then click an ignition marker on the map."}
            </Typography>
          )}
        </Box>
      )}

      {view !== "single" && entry.simulationId && ignitionPoints.length > 1 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <HelpLabel label="Ignition" title="Ignition">
            <p>
              This simulation starts fires from {ignitionPoints.length} ignition
              points. By default the animation shows all of them at once.
            </p>
            <p>
              To focus on one fire, select this card and click its ignition
              marker on the map —{" "}
              {view === "percentile"
                ? "the percentile member's run from just that point plays."
                : "burn probability is then computed for just that point's fire across the ensemble."}{" "}
              Click the marker again (or remove the chip) to go back to all
              ignitions.
            </p>
          </HelpLabel>
          {selectedPoint ? (
            <Chip
              size="small"
              icon={<WhatshotIcon />}
              color="secondary"
              label={`Ignition ${selectedPoint.label}`}
              onDelete={(e) => {
                e.stopPropagation();
                onChange({ partition: null });
              }}
              sx={{ alignSelf: "flex-start" }}
            />
          ) : (
            <Chip
              size="small"
              variant="outlined"
              icon={<WhatshotIcon />}
              label="All ignition points"
              sx={{ alignSelf: "flex-start", color: "text.secondary" }}
            />
          )}
        </Box>
      )}

      {/* Colour: metric palette or a flat tint. */}
      <Box>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Colour
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.5 }}>
          {COLOR_OPTIONS.map((option) => {
            const isActive = (entry.color ?? null) === option.value;
            return (
              <Tooltip key={option.label} title={option.label} arrow>
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange({ color: option.value });
                  }}
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: option.value ?? "transparent",
                    color: "text.secondary",
                    border: isActive
                      ? `2px solid ${TEAL}`
                      : "2px solid rgba(0,0,0,0.2)",
                    boxSizing: "border-box",
                  }}
                >
                  {option.value === null && <PaletteIcon sx={{ fontSize: 14 }} />}
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </Box>

      {/* Transparency: the layer's raster-opacity. */}
      <Box sx={{ px: 0.5 }}>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Opacity
        </Typography>
        <Slider
          size="small"
          min={0.2}
          max={1}
          step={0.05}
          value={entry.opacity ?? 0.85}
          onChange={(_, value) => onChange({ opacity: value as number })}
          onClick={(e) => e.stopPropagation()}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${Math.round(v * 100)}%`}
          sx={{ color: TEAL, mt: -0.5 }}
        />
      </Box>
    </Paper>
  );
}

interface AnimationSceneSettingsProps {
  animations: StorySceneAnimation[];
  /** Entry the builder map's ignition-marker clicks assign the partition to. */
  selectedId: string | null;
  onSelect: (id: string) => void;
  onEntryChange: (id: string, patch: Partial<StorySceneAnimation>) => void;
  onAdd: () => void;
  onRemove: (id: string) => void;
  /** Reorder within the stack: array order = stacking (first = bottom). */
  onMove: (id: string, delta: number) => void;
  scope: StoryScopeInfo;
  lists: ScopeLists;
}

/**
 * Right-rail settings for scene mode "animation": an ordered list of
 * simulation entries overlaid on the main map. All entries share one playback
 * clock; each has its own metric, colour tint, opacity and ensemble view.
 */
export default function AnimationSceneSettings({
  animations,
  selectedId,
  onSelect,
  onEntryChange,
  onAdd,
  onRemove,
  onMove,
  scope,
  lists,
}: AnimationSceneSettingsProps) {
  // "No simulations" is only knowable for a project story; a folder story's
  // entries each pick a project first.
  const ownSims =
    scope.defaultProjectId !== null ? lists.simulationsByProject[String(scope.defaultProjectId)] : undefined;
  const noSimulationsAvailable = !scope.isScoped && !lists.isPending && (ownSims?.length ?? 0) === 0;
  const nothingChosen = animations.every((entry) => !entry.simulationId);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Simulations on this scene
        </Typography>
        <HelpTip title="Animation mode">
          <p>
            This scene plays completed fire simulations over the map. Add more
            than one to compare them — they overlay on a single shared clock,
            drawn in list order with the first at the bottom.
          </p>
          <p>
            Give each one its own colour so the reader can tell them apart, and
            drop the opacity on the upper layers so the ones beneath show
            through.
          </p>
        </HelpTip>
      </Box>

      {noSimulationsAvailable ? (
        <Paper variant="outlined" sx={{ p: 1.5, borderStyle: "dashed" }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            No completed simulations
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Animation scenes play a finished simulation. Run one from the
            project page, then come back — or switch this scene to Layers or
            Compare mode.
          </Typography>
        </Paper>
      ) : (
        nothingChosen && (
          <Paper
            variant="outlined"
            sx={{ p: 1.5, bgcolor: "rgba(0,212,173,0.06)", borderColor: TEAL }}
          >
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Pick a simulation below to see it play on the map.
            </Typography>
          </Paper>
        )
      )}

      {animations.map((entry, index) => (
        <EntryCard
          key={entry.id}
          entry={entry}
          index={index}
          count={animations.length}
          selected={entry.id === selectedId}
          onSelect={() => onSelect(entry.id)}
          onChange={(patch) => onEntryChange(entry.id, patch)}
          onMove={(delta) => onMove(entry.id, delta)}
          onDelete={() => onRemove(entry.id)}
          scope={scope}
          lists={lists}
        />
      ))}

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={onAdd}
        sx={{
          justifyContent: "flex-start",
          textTransform: "none",
          color: "text.secondary",
          border: "1px dashed",
          borderColor: "divider",
        }}
      >
        Add simulation
      </Button>

      {animations.length > 1 && (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Simulations draw in list order — the first sits at the bottom of the
          stack. All entries share one playback clock.
        </Typography>
      )}
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        Frames freeze to a public copy when the story is published.
      </Typography>
    </Box>
  );
}
